import { io } from 'socket.io-client'
import { env } from './env'
import { dispatchForceLogout, refreshSession } from './api'
import { reconnectDelayMs, socketRecoveryAction } from './session-refresh'

/**
 * Lazy-initialised Socket.IO client. Uses the same cookie-based auth
 * as the HTTP API — the access cookie is sent automatically because
 * `withCredentials: true` is set.
 *
 * In dev (`import.meta.env.DEV`), Vite's proxy forwards /socket.io to
 * the API so we connect to the same origin. In prod we use the API URL
 * from VITE_API_URL.
 */

let socket = null

/** In-flight guard for the `connect_error` → refresh → reconnect
 *  loop. Without it, a single expired-cookie reconnect attempt can
 *  trigger a storm: Socket.IO retries the handshake automatically,
 *  each retry fires `connect_error`, each `connect_error` queues
 *  another refresh+reconnect, ad infinitum. The flag drops every
 *  `connect_error` that arrives while the previous recovery cycle
 *  is still working — we only run one recovery at a time. */
let recovering = false

/** Pending self-heal reconnect timer (see `scheduleReconnect`). */
let reconnectTimer = null

/** Consecutive transient recovery failures — drives the backoff in
 *  `scheduleReconnect`. Reset on every successful `connect`. */
let reconnectAttempts = 0

/**
 * Schedule a single delayed reconnect attempt.
 *
 * Critical: when the handshake is rejected by SERVER MIDDLEWARE
 * (`next(new Error('unauthorized'))` in flowdesk-api/src/config/
 * socket.js), Socket.IO marks the connection INACTIVE
 * (`socket.active === false`) and STOPS its automatic reconnection.
 * That means after an auth-rejected handshake the socket will NOT
 * retry on its own — if our refresh+reconnect path doesn't succeed on
 * the first try, realtime stays silently dead until a full page
 * reload. This helper closes that gap: it prompts the socket to try
 * again, and if that attempt also fails the `connect_error` handler
 * re-enters (the `recovering` guard has reset by then) for another
 * refresh — a self-healing backoff loop instead of a dead socket.
 *
 * Coalesced: at most one timer is ever pending, so repeated triggers
 * can't stack into a reconnect storm. [QA-03] Backs off exponentially
 * (3 s → 60 s cap): every attempt costs an `/auth/refresh` POST, and that
 * endpoint is rate-limited per IP for everyone on the network.
 */
function scheduleReconnect() {
  if (reconnectTimer) return
  const delayMs = reconnectDelayMs(reconnectAttempts)
  reconnectAttempts += 1
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (socket && !socket.connected) socket.connect()
  }, delayMs)
}

function resolveSocketUrl() {
  if (import.meta.env.DEV) return undefined
  return env.apiUrl || undefined
}

function getSocket() {
  if (socket) return socket
  socket = io(resolveSocketUrl(), {
    withCredentials: true,
    // [QA-03] Never connect implicitly. Subscribing to an event (e.g. the
    // maintenance store's `maintenance.changed` listener, mounted on every
    // page including /sign-in) must not open a handshake for a signed-out
    // visitor: the server rejects it, the recovery below POSTs
    // /auth/refresh, and a signed-out tab used to repeat that every 3 s
    // forever. Only `connectSocket()` — called by the auth store once a
    // session exists — opens the connection.
    autoConnect: false,
    transports: ['websocket', 'polling'],
    path: '/socket.io',
  })
  socket.on('connect', () => {
    reconnectAttempts = 0
  })
  // Server tells us a session has been terminated (account deleted /
  // tenant suspended / etc.) — fan out through the same force-logout
  // dispatch the axios interceptor uses, so the auth store handles
  // teardown + toast in one place.
  socket.on('session.terminated', ({ reason, message } = {}) => {
    dispatchForceLogout(reason, message)
  })

  // Handshake auth recovery. The backend rejects the Socket.IO
  // handshake with `new Error('unauthorized')` whenever the
  // `taskmagix_access` cookie is missing, expired, or fails JWT
  // verification (see `flowdesk-api/src/config/socket.js`). When that
  // happens we mirror the HTTP layer's behaviour: call `/auth/refresh`
  // through the shared dedupe, and on success kick the socket to
  // reconnect with the fresh access cookie.
  //
  // Why this matters: HTTP requests recover automatically via the
  // axios interceptor, so the app KEEPS WORKING through normal page
  // navigation after the access cookie expires. The socket, however,
  // sits idle until a transport disconnect → reconnect, at which
  // point the handshake fails and realtime stays silently dead.
  // Notifications, invitations, `session.terminated`, and
  // `data:changed` all stop arriving until the user reloads the
  // page. The handler below closes that gap.
  socket.on('connect_error', async (err) => {
    // Only react to AUTH failures. Other `connect_error` causes
    // (network down, server down, TLS issue) are NOT something
    // `/auth/refresh` can fix — Socket.IO's own reconnection
    // logic will keep retrying with backoff. Refreshing on every
    // transport blip would hammer the auth endpoint pointlessly.
    const isAuthError = err?.message === 'unauthorized'
    if (!isAuthError) return
    if (recovering) return
    recovering = true
    try {
      const result = await refreshSession()
      const action = socketRecoveryAction(result)
      if (action === 'reconnect') {
        // Fresh cookie is on the browser. Force a new handshake;
        // Socket.IO picks up the cookie automatically because
        // `withCredentials: true`. Only the first recovery reconnects
        // immediately — if the handshake is rejected again before any
        // successful `connect`, back off instead of refreshing in a
        // tight loop (each pass rotates the refresh token).
        if (reconnectAttempts === 0) {
          reconnectAttempts = 1
          if (socket && !socket.connected) socket.connect()
        } else {
          scheduleReconnect()
        }
        return
      }
      if (action === 'stop') {
        // [QA-03] `{ session: false }` — this browser holds no session
        // cookies, so there is nothing to recover and every retry would
        // be another /auth/refresh POST against the per-IP limit. Stay
        // disconnected; `connectSocket()` reopens on the next sign-in.
        return
      }
      if (action === 'logout') {
        // Session is genuinely dead (account / tenant deleted,
        // password rotated elsewhere, refresh token revoked).
        // Reuse the same force-logout channel the HTTP interceptor
        // uses so the auth store handles teardown + toast in one
        // place. Don't try to reconnect — that would just loop us
        // back here on the next `unauthorized`.
        dispatchForceLogout(
          result.terminalError.code,
          result.terminalError.message,
        )
        return
      }
      // Transient failure (refresh didn't succeed but the session is
      // NOT dead — e.g. a brief wifi blip took out `/auth/refresh`, or
      // the per-IP refresh limiter answered 429).
      // We can't lean on Socket.IO's own reconnection here: the
      // handshake was rejected by server middleware, so the socket is
      // inactive and won't retry by itself. Prompt it on a delay; if it
      // fails again this handler re-enters and refreshes once more,
      // giving a self-healing backoff loop instead of a dead socket.
      scheduleReconnect()
    } finally {
      recovering = false
    }
  })

  return socket
}

/**
 * Subscribe to a server-side event. Returns an unsubscribe function so
 * React effects can clean up.
 */
export function onSocketEvent(event, handler) {
  const s = getSocket()
  s.on(event, handler)
  return () => s.off(event, handler)
}

/** Open the connection eagerly so the `session.terminated` listener is
 *  attached the moment the user is authenticated, not only when a
 *  notifications widget mounts. The auth store calls this on sign-in /
 *  bootstrap success — and it is the ONLY path that connects (the
 *  socket is created with `autoConnect: false`). Idempotent. */
export function connectSocket() {
  const s = getSocket()
  if (!s.connected && !s.active) s.connect()
}

/** Force-disconnect; only used by sign-out. Clears any pending
 *  self-heal reconnect so a timer queued before logout can't open a
 *  fresh socket for a now-signed-out user. */
export function disconnectSocket() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer)
    reconnectTimer = null
  }
  reconnectAttempts = 0
  if (socket) {
    socket.disconnect()
    socket = null
  }
}
